import { db } from '../lib/firebase';
import { doc, getDoc, updateDoc, collection, addDoc, getDocs, query, where, writeBatch } from 'firebase/firestore';
import { Player, Match, PointsHistory } from '../types';

export class RankingService {
  static readonly POINTS_WIN = 20;
  static readonly POINTS_LOSS = 5;
  static readonly ON_FIRE_THRESHOLD = 3;
  static readonly STREAK_BONUS_MULTIPLIER = 1.5;
  static readonly WINDOW_52_WEEKS_MS = 52 * 7 * 24 * 60 * 60 * 1000; // ~1 year in ms

  static async recalculatePlayerPoints(playerId: string): Promise<void> {
    try {
      const historyRef = collection(db, 'players', playerId, 'pointsHistory');
      const q = query(historyRef);
      const snapshot = await getDocs(q);
      
      const now = Date.now();
      const cutoff = now - this.WINDOW_52_WEEKS_MS;
      
      let totalLegacyPoints = 0;
      let globalPoints = 0;
      const clubs: Record<string, number> = {};
      let lastResetTime = 0;

      // Find if there was a recent reset to only count points after it
      snapshot.docs.forEach(docSnap => {
        const entry = docSnap.data() as PointsHistory;
        if ((entry as any).type === 'reset' && entry.createdAt > lastResetTime) {
          lastResetTime = entry.createdAt;
        }
      });

      snapshot.docs.forEach(docSnap => {
        const entry = docSnap.data() as PointsHistory;
        if (entry.createdAt >= cutoff && entry.createdAt >= lastResetTime) {
          if (entry.type === 'match' || entry.type === 'adjustment' || entry.type === 'tournament') {
            const pointsToAdd = entry.points || 0;
            
            totalLegacyPoints += pointsToAdd; // Keep the legacy sum intact
            
            // New logic: Check if it belongs to a circuit
            if (entry.organizerId) {
              if (!clubs[entry.organizerId]) clubs[entry.organizerId] = 0;
              clubs[entry.organizerId] += pointsToAdd;
            } else {
              // Backward compatibility: Old points without organizer go to global, 
              // or maybe we treat them as global by default if not specified.
              globalPoints += pointsToAdd;
            }

            // If explicitly marked as global (e.g. Master tournament)
            if (entry.isGlobal) {
              globalPoints += pointsToAdd;
            }
          }
        }
      });

      const playerRef = doc(db, 'players', playerId);
      const playerSnap = await getDoc(playerRef);
      if (playerSnap.exists()) {
        await updateDoc(playerRef, { 
          points: totalLegacyPoints, // Legacy property preserved for safety
          rankingPoints: {
            global: globalPoints,
            clubs: clubs
          }
        });
      }
    } catch (error) {
      console.error(`Error recalculating points for player ${playerId}:`, error);
    }
  }

  /**
   * Processes points for a completed match.
   */
  static async processMatchPoints(match: Match, skipNotification: boolean = false): Promise<Record<string, number>> {
    if (match.status !== 'completed' || !match.winnerId || match.pointsProcessed) return {};

    try {
      // 1. Fetch Ranking Config
      const configSnap = await getDoc(doc(db, 'ranking_config', 'points_config'));
      const config = configSnap.exists() ? (configSnap.data() as any) : null;

      const pointsWin20 = config?.win20 ?? 40;
      const pointsWin21 = config?.win21 ?? 30;
      const pointsLoss12 = config?.loss12 ?? 10;
      const pointsLoss02 = config?.loss02 ?? 5;
      const streakThreshold = config?.streakThreshold ?? 3;
      const streakMultiplier = config?.streakMultiplier ?? 1.5;

      const batch = writeBatch(db);
      
      // Determine if it was a 3-set match
      const s3 = (match as any).s3 || match.reportedResult?.s3;
      const isThreeSets = s3 && s3 !== '' && s3 !== '0-0';
      const pointsMap: Record<string, number> = {};

      const processPlayer = async (playerId: string, isWinner: boolean) => {
        const playerRef = doc(db, 'players', playerId);
        const playerSnap = await getDoc(playerRef);
        
        if (!playerSnap.exists()) return;
        const p = playerSnap.data() as Player;

        let basePoints = 0;
        if (isWinner) {
          basePoints = isThreeSets ? pointsWin21 : pointsWin20;
        } else {
          basePoints = isThreeSets ? pointsLoss12 : pointsLoss02;
        }

        let pointsAwarded = basePoints;
        let newStreak = p.winStreak || 0;
        let isOnFire = p.isOnFire || false;

        if (isWinner) {
          newStreak += 1;
          if (newStreak >= streakThreshold) {
            isOnFire = true;
          }
          
          pointsAwarded = isOnFire ? Math.floor(basePoints * streakMultiplier) : basePoints;
        } else {
          newStreak = 0;
          isOnFire = false;
          pointsAwarded = basePoints;
        }

        // 1. Update Player Record
        batch.update(playerRef, {
          winStreak: newStreak,
          isOnFire: isOnFire
        });

        // 2. Add History Entry
        const historyRef = doc(collection(db, 'players', playerId, 'pointsHistory'));
        batch.set(historyRef, {
          points: pointsAwarded,
          type: 'match',
          matchId: match.id,
          organizerId: match.organizerId || 'legacy_organizer',
          isGlobal: false, // Individual non-tournament matches don't sum to global by default
          isWin: isWinner,
          createdAt: Date.now()
        });

        // 3. Create Notification
        if (!skipNotification) {
          const notifRef = doc(collection(db, 'notifications'));
          batch.set(notifRef, {
            userId: playerId,
            title: isWinner ? '¡Victoria! Puntos sumados' : 'Partido finalizado',
            body: isWinner 
              ? `¡Felicidades! Has conseguido +${pointsAwarded} puntos de ranking por tu victoria${match.tournamentName ? ` en el torneo ${match.tournamentName}` : ''} (${isThreeSets ? '2-1' : '2-0'} en sets)${isOnFire ? ' + Bono Racha' : ''}.` 
              : `Has sumado +${pointsAwarded} puntos de ranking por participación${match.tournamentName ? ` en el torneo ${match.tournamentName}` : ''} (${isThreeSets ? '1-2' : '0-2'} en sets).`,
            points: pointsAwarded,
            type: 'ranking_points',
            matchId: match.id,
            read: false,
            createdAt: Date.now()
          });
        }
        pointsMap[playerId] = pointsAwarded;
      };

      // Resolve all actual player IDs involved
      const participants = match.participantIds || [match.team1Id, match.team2Id];
      const playersToProcess: { id: string, isWinner: boolean }[] = [];

      for (const id of participants) {
        // Try as player first
        const pSnap = await getDoc(doc(db, 'players', id));
        if (pSnap.exists()) {
          const isWinner = (match as any).winnerPlayerIds 
            ? (match as any).winnerPlayerIds.includes(id) 
            : match.winnerId === id;
          playersToProcess.push({ id, isWinner });
        } else {
          // Try as team
          const tSnap = await getDoc(doc(db, 'teams', id));
          if (tSnap.exists()) {
            const team = tSnap.data();
            const isWinner = match.winnerId === id;
            if (team.player1Id) playersToProcess.push({ id: team.player1Id, isWinner });
            if (team.player2Id) playersToProcess.push({ id: team.player2Id, isWinner });
            if (team.player3Id) playersToProcess.push({ id: team.player3Id, isWinner });
          }
        }
      }

      for (const pInfo of playersToProcess) {
        await processPlayer(pInfo.id, pInfo.isWinner);
      }

      // Mark match as processed
      const matchRef = doc(db, 'matches', match.id);
      batch.update(matchRef, { pointsProcessed: true });

      await batch.commit();

      // Recalculate 52-weeks sum for all resolved players
      for (const pInfo of playersToProcess) {
        await this.recalculatePlayerPoints(pInfo.id);
      }
      return pointsMap;

    } catch (error) {
      console.error(`Error processing match points for match ${match.id}:`, error);
      return {};
    }
  }

  /**
   * Processes points for a completed public matchmaking match.
   */
  static async processPublicMatchPoints(matchId: string, pair1: string[], pair2: string[], winnerPair: 1 | 2, s3?: string, skipNotification: boolean = false): Promise<Record<string, number>> {
    try {
      // 1. Fetch Ranking Config
      const configSnap = await getDoc(doc(db, 'ranking_config', 'points_config'));
      const config = configSnap.exists() ? (configSnap.data() as any) : null;

      const pointsWin20 = config?.win20 ?? 40;
      const pointsWin21 = config?.win21 ?? 30;
      const pointsLoss12 = config?.loss12 ?? 10;
      const pointsLoss02 = config?.loss02 ?? 5;
      const streakThreshold = config?.streakThreshold ?? 3;
      const streakMultiplier = config?.streakMultiplier ?? 1.5;

      const batch = writeBatch(db);
      
      const isThreeSets = s3 && s3 !== '' && s3 !== '0-0';
      const pointsMap: Record<string, number> = {};

      const processPlayer = async (playerId: string, isWinner: boolean) => {
        if (playerId.startsWith('guest_')) return;

        const playerRef = doc(db, 'players', playerId);
        const playerSnap = await getDoc(playerRef);
        
        if (!playerSnap.exists()) return;
        const p = playerSnap.data() as Player;

        let basePoints = 0;
        if (isWinner) {
          basePoints = isThreeSets ? pointsWin21 : pointsWin20;
        } else {
          basePoints = isThreeSets ? pointsLoss12 : pointsLoss02;
        }

        let pointsAwarded = basePoints;
        let newStreak = p.winStreak || 0;
        let isOnFire = p.isOnFire || false;

        if (isWinner) {
          newStreak += 1;
          if (newStreak >= streakThreshold) {
            isOnFire = true;
          }
          
          pointsAwarded = isOnFire ? Math.floor(basePoints * streakMultiplier) : basePoints;
        } else {
          newStreak = 0;
          isOnFire = false;
          pointsAwarded = basePoints;
        }

        // 1. Update Player Record
        batch.update(playerRef, {
          winStreak: newStreak,
          isOnFire: isOnFire
        });

        // 2. Add History Entry
        const historyRef = doc(collection(db, 'players', playerId, 'pointsHistory'));
        batch.set(historyRef, {
          points: pointsAwarded,
          type: 'match',
          matchId: matchId,
          organizerId: 'public_match', // We don't have the clubId strictly available here in the args, but public matches usually don't count towards clubs, or we can just say 'public'
          isGlobal: false,
          isWin: isWinner,
          createdAt: Date.now()
        });

        // 3. Create Notification
        if (!skipNotification) {
          const notifRef = doc(collection(db, 'notifications'));
          batch.set(notifRef, {
            userId: playerId,
            title: isWinner ? '¡Victoria Oficial!' : 'Partido Oficial finalizado',
            body: isWinner 
              ? `¡Felicidades! Has conseguido +${pointsAwarded} puntos de ranking por tu victoria en Partido Oficial (${isThreeSets ? '2-1' : '2-0'} en sets)${isOnFire ? ' + Bono Racha' : ''}.` 
              : `Has sumado +${pointsAwarded} puntos de ranking por participación en Partido Oficial (${isThreeSets ? '1-2' : '0-2'} en sets).`,
            points: pointsAwarded,
            type: 'ranking_points',
            matchId: matchId,
            read: false,
            createdAt: Date.now()
          });
        }
        pointsMap[playerId] = pointsAwarded;
      };

      const playersToProcess: { id: string; isWinner: boolean }[] = [];
      pair1.forEach(id => {
        playersToProcess.push({ id, isWinner: winnerPair === 1 });
      });
      pair2.forEach(id => {
        playersToProcess.push({ id, isWinner: winnerPair === 2 });
      });

      for (const pInfo of playersToProcess) {
        await processPlayer(pInfo.id, pInfo.isWinner);
      }

      await batch.commit();

      // Recalculate 52-weeks sum for all resolved players
      for (const pInfo of playersToProcess) {
        if (!pInfo.id.startsWith('guest_')) {
          await this.recalculatePlayerPoints(pInfo.id);
        }
      }
      return pointsMap;

    } catch (error) {
      console.error(`Error processing public match points for match ${matchId}:`, error);
      return {};
    }
  }

  /**
   * Reverts points for a previously completed match.
   */
  static async revertMatchPoints(matchId: string, participantIds: string[]): Promise<void> {
    try {
      const batch = writeBatch(db);

      for (const playerId of participantIds) {
        if (playerId.startsWith('guest_')) continue;

        // 1. Find the points history entry for this match
        const historyRef = collection(db, 'players', playerId, 'pointsHistory');
        const q = query(historyRef, where('matchId', '==', matchId));
        const snapshot = await getDocs(q);

        snapshot.docs.forEach(docSnap => {
          batch.delete(docSnap.ref);
        });
      }

      // We might need to update match as pointsProcessed = false, 
      // but usually the match is being updated elsewhere. We will do it here if possible.
      const matchRef = doc(db, 'matches', matchId);
      // We will just try to update pointsProcessed, if it fails because it's a public match, it's ok.
      try {
         batch.update(matchRef, { pointsProcessed: false });
      } catch (e) {
         // ignore
      }

      await batch.commit();

      // Recalculate 52-weeks sum for all resolved players
      for (const playerId of participantIds) {
        if (!playerId.startsWith('guest_')) {
          await this.recalculatePlayerPoints(playerId);
        }
      }
    } catch (error) {
      console.error(`Error reverting match points for match ${matchId}:`, error);
    }
  }

  /**
   * Resets a player's points and streak (used when changing category).
   */
  static async resetPlayerPoints(playerId: string, reason: string): Promise<void> {
    try {
      const batch = writeBatch(db);
      const playerRef = doc(db, 'players', playerId);
      
      batch.update(playerRef, {
        points: 0,
        winStreak: 0,
        isOnFire: false
      });

      const historyRef = doc(collection(db, 'players', playerId, 'pointsHistory'));
      batch.set(historyRef, {
        points: 0,
        type: 'reset',
        reason: reason,
        createdAt: Date.now()
      });

      await batch.commit();
    } catch (error) {
      console.error(`Error resetting points for player ${playerId}:`, error);
    }
  }
}
